// Cybras theme - GSAP ScrollTrigger motion layer.
//
// Vendored: GSAP 3.15.0 (assets/gsap.min.js + assets/ScrollTrigger.min.js),
// downloaded from the `gsap` npm package (registry tarball
// https://registry.npmjs.org/gsap/-/gsap-3.15.0.tgz - GSAP has shipped every
// plugin free under the GreenSock "no charge" license since v3.12). No CDN:
// Shopify serves these two files as ordinary theme assets, same as theme.js.
//
// GSAP IS LOADED ON DEMAND, NOT ON EVERY PAGE. The two vendored files are
// 115 KB together, against a 2.4 KB theme.js - dropping that onto a product
// page that animates nothing is a real cost on a retail storefront. So this
// file is the only one the layout loads eagerly; it looks for a single
// [data-parallax] element and injects GSAP + ScrollTrigger only if it finds
// one. Pages with no scroll effects pay 7 KB instead of 122 KB.
//
// This file EXTENDS the existing reveal system in theme.js, it does not
// replace or duplicate it:
//   - theme.js's IntersectionObserver + [data-reveal]/[data-reveal-delay] is
//     the ONLY thing that ever sets opacity. That contract is unchanged.
//   - ScrollTrigger here only drives scroll-LINKED transforms (translate /
//     scale / rotate) on elements that opt in via [data-parallax]. It must
//     never touch opacity - a sibling project already got burned by mixing
//     the two systems, which left lower blocks stuck at opacity: 0 because
//     ScrollTrigger's own inline styles fought the reveal CSS.
//   - bindOne() below refuses to bind an element that also carries
//     [data-reveal], so the two systems physically cannot collide even if a
//     future section author adds both attributes by mistake.
//
// Opt-in contract for section/snippet authors (nothing uses this yet):
//   data-parallax="up|down|scale|rotate"   required, selects the effect
//   data-parallax-amount="N"               optional, effect-specific unit
//                                           (px for up/down, % for scale,
//                                           degrees for rotate); each effect
//                                           has a sensible default.
//
// Hard requirements enforced here:
//   - prefers-reduced-motion: reduce -> every effect no-ops, via
//     gsap.matchMedia scoped to "(prefers-reduced-motion: no-preference)".
//     The gate is checked BEFORE the loader runs too, so a reduced-motion
//     visitor never downloads GSAP at all.
//   - No JavaScript -> the page is already fully readable/functional, since
//     [data-parallax] elements render at their normal, static position with
//     no inline styles until this file runs.
//   - GSAP/ScrollTrigger missing, blocked or throwing -> fails silently,
//     never throws past this file.

(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var started = false;

  function prefersReducedMotion() {
    return !!(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  // ---- The motion layer proper -------------------------------------------
  // Runs only once GSAP + ScrollTrigger are present on window.

  function init() {
    try {
      gsap.registerPlugin(ScrollTrigger);

      // ---- Effect registry -------------------------------------------------
      // Every effect is transform-only and scroll-scrubbed (tied directly to
      // scroll position, not eased playback), so it never fights the
      // reveal system's opacity/transform transitions on unrelated elements.
      var DEFAULT_AMOUNT = { up: 60, down: 60, scale: 12, rotate: 6 };

      var EFFECTS = {
        up: function (el, amount) {
          gsap.fromTo(
            el,
            { y: amount },
            {
              y: -amount,
              ease: 'none',
              scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true },
            }
          );
        },
        down: function (el, amount) {
          gsap.fromTo(
            el,
            { y: -amount },
            {
              y: amount,
              ease: 'none',
              scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true },
            }
          );
        },
        scale: function (el, amount) {
          var from = 1 - Math.min(amount, 40) / 100;
          gsap.fromTo(
            el,
            { scale: from },
            {
              scale: 1,
              ease: 'none',
              scrollTrigger: { trigger: el, start: 'top bottom', end: 'center center', scrub: true },
            }
          );
        },
        rotate: function (el, amount) {
          gsap.fromTo(
            el,
            { rotate: -amount },
            {
              rotate: amount,
              ease: 'none',
              scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true },
            }
          );
        },
      };

      // One matchMedia instance for the whole page. Every batch of triggers is
      // registered through mm.add(), which both gates it on the query AND
      // reverts (kills) it automatically the moment the query stops matching -
      // e.g. the OS toggles reduced-motion on mid-session.
      var mm = gsap.matchMedia();

      function bindOne(el) {
        if (el.dataset.parallaxBound === 'true') return;

        if ('reveal' in el.dataset) {
          // Belt-and-suspenders: this file should never be handed a
          // [data-reveal] element (opacity stays theme.js's alone), but if a
          // future section author adds [data-parallax] to one by mistake,
          // skip it loudly instead of silently fighting the reveal system.
          if (window.console && console.warn) {
            console.warn(
              '[motion.js] Skipping [data-parallax] on a [data-reveal] element - the two systems must not combine.',
              el
            );
          }
          return;
        }

        var type = (el.dataset.parallax || '').trim();
        var effect = EFFECTS[type];
        if (!effect) return;

        var amount = parseFloat(el.dataset.parallaxAmount);
        if (!isFinite(amount)) amount = DEFAULT_AMOUNT[type];

        el.dataset.parallaxBound = 'true';
        effect(el, amount);
      }

      // ---- Stroke draw ------------------------------------------------------
      // Scrubs an SVG path's stroke-dashoffset so the line draws itself as the
      // reader scrolls. Used by snippets/measure-diagram.liquid for the tape
      // measure. Deliberately NOT set in the markup: with no JavaScript, no
      // GSAP, or under reduced motion the path renders complete, because
      // nothing ever wrote a dasharray onto it.
      function bindDraw(el) {
        if (el.dataset.drawBound === 'true') return;
        if (typeof el.getTotalLength !== 'function') return;

        var len = el.getTotalLength();
        if (!len) return;

        el.dataset.drawBound = 'true';
        gsap.set(el, { strokeDasharray: len, strokeDashoffset: len });
        gsap.to(el, {
          strokeDashoffset: 0,
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top 92%', end: 'top 45%', scrub: true },
        });
      }

      function scan(root) {
        var scope = root && root.querySelectorAll ? root : document;
        var els = scope.querySelectorAll('[data-parallax]');
        var draws = scope.querySelectorAll('[data-draw]');
        if (!els.length && !draws.length) return;

        mm.add('(prefers-reduced-motion: no-preference)', function () {
          Array.prototype.forEach.call(els, bindOne);
          Array.prototype.forEach.call(draws, bindDraw);
          // No cleanup function returned: the ScrollTriggers created inside
          // bindOne()/bindDraw() are torn down automatically by matchMedia's
          // own revert() if this query ever stops matching. GSAP's revert also
          // strips the inline dash properties, so the path returns to complete.
        });
      }

      scan(document);

      // The theme editor adds, removes and reorders sections without a page
      // load (same reason theme.js re-observes reveals on these events). New
      // [data-parallax] elements need binding; anything already bound is
      // skipped by the dataset guard above, so this is safe to call repeatedly.
      document.addEventListener('shopify:section:load', function (event) {
        scan(event.target);
        ScrollTrigger.refresh();
      });
      document.addEventListener('shopify:section:select', function (event) {
        scan(event.target);
        ScrollTrigger.refresh();
      });
      document.addEventListener('shopify:section:reorder', function () {
        ScrollTrigger.refresh();
      });
      document.addEventListener('shopify:section:unload', function () {
        ScrollTrigger.refresh();
      });
    } catch (err) {
      // Never let a motion-layer failure break the storefront.
      return;
    }
  }

  // ---- On-demand loader ---------------------------------------------------

  function loadScript(src, done) {
    var s = document.createElement('script');
    s.src = src;
    s.async = false; // preserve gsap-before-ScrollTrigger execution order
    s.onload = done;
    s.onerror = function () {
      /* asset blocked or missing - stay silent, the page is fine without it */
    };
    document.head.appendChild(s);
  }

  function boot() {
    if (started) return;

    // The gate. No opted-in element on this page means no GSAP download.
    if (!document.querySelector('[data-parallax], [data-draw]')) return;

    // A reduced-motion visitor would have every effect no-op anyway, so skip
    // the 115 KB entirely rather than downloading it to do nothing.
    if (prefersReducedMotion()) return;

    started = true;

    if (window.gsap && window.ScrollTrigger) {
      init();
      return;
    }

    // The layout hands us the asset_url of each vendored file on this
    // script's own tag, because Liquid filters cannot be evaluated from JS.
    var tag = document.querySelector('script[data-gsap-src]');
    if (!tag) return;

    var gsapSrc = tag.getAttribute('data-gsap-src');
    var stSrc = tag.getAttribute('data-scrolltrigger-src');
    if (!gsapSrc || !stSrc) return;

    loadScript(gsapSrc, function () {
      loadScript(stSrc, function () {
        if (window.gsap && window.ScrollTrigger) init();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // In the theme editor a section carrying the first [data-parallax] element
  // on the page can appear after boot() already declined to load anything.
  document.addEventListener('shopify:section:load', boot);
})();
